import React from 'react';
import {ButtonLoader, ButtonLoaderSize} from '../button-loader';

export enum ButtonVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Outline = 'outline',
  Ghost = 'ghost',
  Danger = 'danger',
  Success = 'success',
}

export enum ButtonSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

export enum ButtonType {
  BUTTON = 'button',
  SUBMIT = 'submit',
  RESET = 'reset',
}

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const variantClasses = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white border-transparent focus:ring-2 focus:ring-blue-200',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-transparent focus:ring-2 focus:ring-gray-200',
  outline: 'bg-transparent hover:bg-blue-50 text-blue-600 border-blue-600 focus:ring-2 focus:ring-blue-200',
  ghost: 'bg-transparent hover:bg-gray-100 text-gray-600 border-transparent focus:ring-2 focus:ring-gray-200',
  danger: 'bg-red-600 hover:bg-red-700 text-white border-transparent focus:ring-2 focus:ring-red-200',
  success: 'bg-green-600 hover:bg-green-700 text-white border-transparent focus:ring-2 focus:ring-green-200',
};

const sizeClasses = {
  xs: 'px-2.5 py-1.5 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-3 text-base',
  lg: 'px-5 py-4 text-lg',
  xl: 'px-6 py-5 text-xl',
};

const disabledClasses = 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed text-white border-gray-400';

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = ButtonVariant.Primary,
  size = ButtonSize.MD,
  type = ButtonType.BUTTON,
  disabled = false,
  loading = false,
  loadingText,
  startIcon,
  endIcon,
  fullWidth = false,
  className = '',
  onClick,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const baseClasses = [
    'inline-flex items-center justify-center gap-2',
    'font-medium rounded-full border',
    'transition-all duration-200 ease-in-out',
    'focus:outline-none focus:ring-4 focus:ring-opacity-50',
    'disabled:cursor-not-allowed',
    sizeClasses[size],
    fullWidth ? 'w-full' : '',
    isDisabled ? disabledClasses : variantClasses[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && onClick) {
      onClick(event);
    }
  };

  return (
    <button type={type} disabled={isDisabled} className={baseClasses} onClick={handleClick} {...props}>
      {loading ? (
        <>
          <ButtonLoader size={ButtonLoaderSize.Small} dotCount={3} />
          <span>{loadingText || children}</span>
        </>
      ) : (
        <>
          {startIcon && <span className="flex items-center">{startIcon}</span>}
          <span>{children}</span>
          {endIcon && <span className="flex items-center">{endIcon}</span>}
        </>
      )}
    </button>
  );
};
